import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, SimpleChanges, inject, input, model, signal } from '@angular/core';

import { BehaviorSubject, Observable, distinctUntilChanged, filter, map } from 'rxjs';

import { InputChoicesComponent } from '@camelot/form-input';
import { InputChoices, InputChoicesOption } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { HandleSimpleRequest } from '@camelot/server';
import { Project } from '@camelot/services';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  CardTitleComponent,
  EmptyComponent,
  ErrorComponent,
  LoaderComponent,
  TitleComponent,
} from '@camelot/ui';
import { CamBaseComponent, extractSingleValue, isNonNullable } from '@camelot/utils';

import { CamTranslationProject } from '../../../../translation.service';
import { ProjectExtended } from '../../services/dto/project';
import { CamProjectsService } from '../../services/projects.service';
import { ProjectProfileComponent } from './profile/project-profile.component';

@Component({
  selector: 'cam-project-profile-card',
  imports: [
    AsyncPipe,
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    CardHeaderComponent,
    CardTitleComponent,
    EmptyComponent,
    ErrorComponent,
    FontIconComponent,
    InputChoicesComponent,
    LoaderComponent,
    ProjectProfileComponent,
    TitleComponent,
    TranslatePipe,
  ],
  templateUrl: './project-profile-card.component.html',
  styleUrl: './project-profile-card.component.scss',
})
export class ProjectProfileCardComponent extends CamBaseComponent {
  projectId = model.required<string | null>();

  navigationLink = input<string | null>(null);

  editMode = input(false);

  clientId = input<string | null>(null);

  private readonly _projectsService = inject(CamProjectsService);

  private readonly _options$ = new BehaviorSubject<InputChoicesOption[]>([]);

  public readonly isEditing = signal<boolean>(false);
  public readonly project$ = signal<Observable<ProjectExtended | null> | null>(null);
  public readonly selectedId = signal<string | null>(null);
  public projectPreferenceById = new HandleSimpleRequest<ProjectExtended | null>();
  public projectSelect: InputChoices | null = null;

  constructor() {
    super();
    CamTranslationProject.getInstance();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId']) {
      this._fetchProjects(this.clientId());
    }
  }

  ngOnInit(): void {
    this.selectedId.set(this.projectId() ?? null);
    this.project$.set(this.projectPreferenceById.get$());
    this._fetch(this.projectId());
  }

  public navigate() {
    const navigationLink = this.navigationLink();
    if (navigationLink) {
      this._router.navigateByUrl(navigationLink);
    }
  }

  public startEditing() {
    this._buildSelector();
    this.isEditing.set(true);
  }

  private _buildSelector() {
    const currentId = this.selectedId();
    this.projectSelect = new InputChoices({
      ...(this.clientId()
        ? { options: this._options$.asObservable() }
        : {
            advancedSearch$: (search?: string | null, selectId?: string[]) =>
              this._projectsService
                .fetchSearchProjects$(search, selectId)
                .pipe(map(projects => projects.map(project => this._toProjectOption(project)))),
          }),
      key: 'projectId',
      multiple: false,
      value: currentId ? [currentId] : [],
      withSearch: true,
    });

    this._registerSubscription(
      this.projectSelect.changeValue$
        .pipe(
          map(value => extractSingleValue(value)),
          distinctUntilChanged()
        )
        .subscribe(newId => {
          this.isEditing.set(false);
          if (newId === this.selectedId()) {
            return;
          }
          this.selectedId.set(newId);
          this._fetch(newId);
          this.projectId.set(newId);
        })
    );
  }

  private _fetchProjects(clientId: string | null) {
    if (!clientId) {
      this._options$.next([]);
      return;
    }
    this._registerSubscription(
      this._projectsService
        .fetchProjectsByContact$(clientId)
        .pipe(filter(isNonNullable))
        .subscribe(projects => this._options$.next(projects.map(project => this._toProjectOption(project))))
    );
  }

  private _toProjectOption(project: Project): InputChoicesOption {
    return { data: project, id: project.id, name: project.name };
  }

  private _fetch(id: string | null) {
    if (!id) {
      return;
    }
    this.requestState.asked();
    this._registerSubscription(
      this.projectPreferenceById
        .fetch(this._projectsService.fetchDynamicProps$(id, ['name', 'department', 'address', 'status', 'company']))
        .subscribe({
          complete: () => this.requestState.completed(),
          error: (error: HttpErrorResponse) => {
            this.requestState.onError(error.status, error.statusText);
          },
        })
    );
  }
}
